import React from 'react';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { AlertTriangle, Clock, X, CheckCircle } from 'lucide-react';

interface TailoringProcessingModalProps {
  isOpen: boolean;
  elapsedTime: number;
  progressPercentage: number;
  isComplete: boolean;
  onCancel: () => void;
}

export function TailoringProcessingModal({
  isOpen,
  elapsedTime,
  progressPercentage,
  isComplete,
  onCancel,
}: TailoringProcessingModalProps) {
  // Format time as MM:SS
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  // Get current stage based on progress
  const getCurrentStage = () => {
    if (progressPercentage >= 85) return 'Finalizing';
    if (progressPercentage >= 70) return 'Optimizing';
    if (progressPercentage >= 40) return 'Tailoring';
    if (progressPercentage >= 15) return 'Analyzing';
    return 'Starting';
  };

  return (
    <Dialog open={isOpen} modal>
      <DialogContent 
        className="sm:max-w-lg p-0 gap-0 border-0 bg-transparent shadow-none [&>button]:hidden"
        onPointerDownOutside={(e) => e.preventDefault()}
        onEscapeKeyDown={(e) => e.preventDefault()}
      >
        <div className="bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 rounded-2xl shadow-2xl border border-slate-700 overflow-hidden">
          {/* Header with Warning */}
          <div className="bg-gradient-to-r from-amber-500/20 to-orange-500/20 border-b border-amber-500/30 p-6">
            <div className="flex items-center justify-center gap-3 mb-3">
              <div className="w-12 h-12 rounded-full bg-amber-500/20 flex items-center justify-center animate-pulse">
                <AlertTriangle className="w-7 h-7 text-amber-400" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-center text-white">
              Tailoring In Progress
            </h2>
          </div>

          {/* Warning Message */}
          <div className="p-6 text-center">
            <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 mb-6">
              <p className="text-amber-200 text-sm leading-relaxed">
                <span className="font-semibold">You can leave this page</span>
                <br />
                Tailoring keeps running while you use the rest of TailorWiz. Your results open when it's done. Just keep this browser tab open.
              </p>
            </div>

            {/* Timer and Progress Display */}
            <div className="grid grid-cols-2 gap-6 mb-6">
              {/* Timer */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center justify-center gap-2 mb-1">
                  <Clock className="w-5 h-5 text-blue-400" />
                  <span className="text-slate-400 text-sm">Elapsed Time</span>
                </div>
                <div className="text-3xl font-bold font-mono text-white">
                  {formatTime(elapsedTime)}
                </div>
              </div>

              {/* Percentage */}
              <div className="bg-slate-800/50 rounded-xl p-4 border border-slate-700">
                <div className="flex items-center justify-center gap-2 mb-1">
                  {isComplete ? (
                    <CheckCircle className="w-5 h-5 text-green-400" />
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-blue-400 border-t-transparent animate-spin" />
                  )}
                  <span className="text-slate-400 text-sm">Progress</span>
                </div>
                <div className={`text-3xl font-bold ${isComplete ? 'text-green-400' : 'text-white'}`}>
                  {progressPercentage.toFixed(0)}%
                </div>
              </div>
            </div>

            {/* Progress Bar */}
            <div className="mb-4">
              <div className="relative w-full h-4 bg-slate-700 rounded-full overflow-hidden">
                <div 
                  className={`absolute top-0 left-0 h-full rounded-full transition-all duration-500 ease-out ${
                    isComplete 
                      ? 'bg-gradient-to-r from-green-500 to-emerald-400' 
                      : 'bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-500'
                  }`}
                  style={{ width: `${progressPercentage}%` }}
                />
                {/* Animated shine */}
                {!isComplete && (
                  <div 
                    className="absolute top-0 h-full w-16 bg-gradient-to-r from-transparent via-white/20 to-transparent"
                    style={{ 
                      left: `${Math.max(0, progressPercentage - 10)}%`,
                      animation: 'shimmer 2s infinite',
                    }}
                  />
                )}
              </div>
            </div>

            {/* Stage Indicators */}
            <div className="flex justify-between text-xs mb-6">
              <span className={`px-2 py-1 rounded ${progressPercentage >= 15 ? 'text-green-400 font-medium' : 'text-slate-500'}`}>
                {progressPercentage >= 15 && '✓ '}Analyzing
              </span>
              <span className={`px-2 py-1 rounded ${progressPercentage >= 40 ? 'text-green-400 font-medium' : 'text-slate-500'}`}>
                {progressPercentage >= 40 && '✓ '}Tailoring
              </span>
              <span className={`px-2 py-1 rounded ${progressPercentage >= 70 ? 'text-green-400 font-medium' : 'text-slate-500'}`}>
                {progressPercentage >= 70 && '✓ '}Optimizing
              </span>
              <span className={`px-2 py-1 rounded ${progressPercentage >= 85 ? 'text-green-400 font-medium' : 'text-slate-500'}`}>
                {progressPercentage >= 85 ? (isComplete ? '✓ Complete' : 'Finalizing...') : 'Finalizing'}
              </span>
            </div>

            {/* Current Status */}
            <div className="text-slate-300 text-sm mb-6">
              {isComplete ? (
                <span className="text-green-400 font-medium">✨ Tailoring complete! Redirecting...</span>
              ) : (
                <span>Currently {getCurrentStage().toLowerCase()}...</span>
              )}
            </div>

            {/* Cancel Button */}
            {!isComplete && (
              <Button
                onClick={onCancel}
                variant="outline"
                className="border-red-500/50 text-red-400 hover:bg-red-500/10 hover:text-red-300 hover:border-red-400"
              >
                <X className="w-4 h-4 mr-2" />
                Stop Tailoring
              </Button>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
